import { AnimationPicker } from '@/components/admin/experiences/page-editor/animation-picker';
import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { Button } from '@/components/ui/button';
import { SPEED_LABEL, tempsSummary } from '@/lib/page-editor';
import type { BlockAnimation, ExperiencePageDraft } from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * L'onglet **Animation** d'un bloc (refonte modulaire §9.3; planche
 * `EditeurPageTelephone`; issue #58).
 *
 * « L'animation d'entrée par icônes, la rapidité EN LECTURE - "rapide, héritée
 * du temps 1", avec le lien "Régler le temps 1" - et le sélecteur "Apparaît au
 * temps", qui reste le geste sûr à côté du glisser-déposer. »
 *
 * The speed is read-only here and that is the sentence of H27 rendered: « un
 * bloc ne force pas sa rapidité : elle est celle de son temps ». A control here
 * would be a second place to set one number, and the two would disagree for
 * whoever looks at the pile.
 *
 * A Son carries only « Apparaît au temps » (§9.3: « un son n'en a pas »), which
 * is why the picker is behind a branch rather than greyed: a greyed row of
 * eight icons asks what it would do if it were enabled.
 */

export interface AnimationPanelProps {
  readonly page: ExperiencePageDraft;
  readonly blockId: string;
  readonly animation: BlockAnimation;
  readonly tempsId: string;
  readonly readOnly: boolean;
  /** A Son has no entry to play (§9.3). */
  readonly animated: boolean;
  readonly onAnimation: (next: BlockAnimation) => void;
  readonly onTemps: (tempsId: string) => void;
  readonly onOpenTemps: (tempsId: string) => void;
}

export function AnimationPanel({
  page,
  blockId,
  animation,
  tempsId,
  readOnly,
  animated,
  onAnimation,
  onTemps,
  onOpenTemps,
}: AnimationPanelProps) {
  const selectId = useId();
  const index = page.temps.findIndex((one) => one.id === tempsId);
  const temps = page.temps[index] ?? null;

  return (
    <div className="space-y-3">
      {animated ? (
        <div className="space-y-1.5">
          <p className="block font-semibold text-gray-800 text-xs">Animation d’entrée</p>
          <AnimationPicker
            label={`Animation d’entrée du bloc ${blockId}`}
            value={animation}
            readOnly={readOnly}
            onChange={onAnimation}
          />
          <p className="text-gray-600 text-xs leading-4">
            Un clic rejoue l’animation dans l’aperçu.
          </p>
        </div>
      ) : (
        <p className="text-gray-600 text-xs leading-4">
          Un son n’a pas d’animation d’entrée : il part au premier toucher sur la page.
        </p>
      )}

      {temps === null ? null : (
        <div className="space-y-1.5">
          <p className="block font-semibold text-gray-800 text-xs">Rapidité</p>
          <p className="text-gray-800 text-sm">
            <span className="font-semibold">{SPEED_LABEL[temps.speed]}</span>, héritée du temps{' '}
            {index + 1}
          </p>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenTemps(temps.id)}>
            Régler le temps {index + 1}
          </Button>
          <p className="text-gray-600 text-xs leading-4">
            La rapidité appartient au temps : tous ses blocs bougent au même tempo, chacun avec son
            mouvement.
          </p>
        </div>
      )}

      <div className="space-y-1.5">
        <label htmlFor={selectId} className="block font-semibold text-gray-800 text-xs">
          Apparaît au temps
        </label>
        <select
          id={selectId}
          data-testid={PAGE_EDITOR_IDS.blockTemps}
          value={tempsId}
          disabled={readOnly}
          onChange={(event) => onTemps(event.currentTarget.value)}
          className="h-9 w-full cursor-pointer rounded-md border-2 border-gray-300 bg-white px-2 text-sm focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:bg-gray-50"
        >
          {page.temps.map((one, at) => (
            <option key={one.id} value={one.id}>
              {tempsSummary(one, at)}
            </option>
          ))}
        </select>
        <p className="text-gray-600 text-xs leading-4">
          Glisser le bloc sur un séparateur de la pile fait le même geste.
        </p>
      </div>
    </div>
  );
}
